import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import { useMobileDetect } from '../../hooks/useMobileDetect';
import {
  API_BASE,
  getRoomAuthenticated,
  Room,
  Document as DocumentType,
  PageMapping,
  Project,
  getPageMappingsAuthenticated,
  createPageMapping,
  deletePageMapping,
  updatePageMapping,
  updateRoom,
  getDocuments,
  getDocumentSetsAuthenticated,
  getProjects,
  DocumentSet
} from '../../api/client';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export function RoomDetail() {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState<Room | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageMappings, setPageMappings] = useState<PageMapping[]>([]);
  const [documentSets, setDocumentSets] = useState<DocumentSet[]>([]);
  const [selectedDocumentSet, setSelectedDocumentSet] = useState<number | null>(null);
  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [editingMappingId, setEditingMappingId] = useState<number | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [editingRoom, setEditingRoom] = useState(false);
  const [editRoomNumber, setEditRoomNumber] = useState('');
  const [editRoomName, setEditRoomName] = useState('');
  const [editIsExterior, setEditIsExterior] = useState(false);
  const [savingRoom, setSavingRoom] = useState(false);
  const qrRef = useRef<HTMLAnchorElement>(null);
  const isMobile = useMobileDetect();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      if (!token || !projectId || !roomId) return;
      try {
        const [roomData, mappings, docSets, projects] = await Promise.all([
          getRoomAuthenticated(token, parseInt(projectId), parseInt(roomId)),
          getPageMappingsAuthenticated(token, parseInt(roomId)),
          getDocumentSetsAuthenticated(token, parseInt(projectId)),
          getProjects(token)
        ]);
        setRoom(roomData);
        setPageMappings(mappings);
        setDocumentSets(docSets);
        const foundProject = projects.find(p => p.id === parseInt(projectId));
        if (foundProject) {
          setProject(foundProject);
        }
        if (docSets.length > 0) {
          setSelectedDocumentSet(docSets[0].id);
        }
      } catch (error) {
        console.error('Failed to load room data:', error);
        navigate(`/admin/projects/${projectId}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, roomId, token, isAuthenticated, navigate]);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!token || !selectedDocumentSet) return;
      try {
        const docs = await getDocuments(token, selectedDocumentSet);
        setDocuments(docs);
      } catch (error) {
        console.error('Failed to load documents:', error);
      }
    };

    loadDocuments();
  }, [selectedDocumentSet, token]);

  const handleDownloadQR = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      const downloadNumber = room?.is_exterior ? `EXT-${room.number}` : room?.number;
      downloadLink.download = `room-${downloadNumber}-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleDeleteMapping = async (mappingId: number) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this page mapping?')) return;
    try {
      await deletePageMapping(token, mappingId);
      setPageMappings(prev => prev.filter(m => m.id !== mappingId));
    } catch (error) {
      console.error('Failed to delete mapping:', error);
    }
  };

  const handleStartEdit = (mapping: PageMapping) => {
    setEditingMappingId(mapping.id);
    setEditingLabel(mapping.label || '');
  };

  const handleCancelEdit = () => {
    setEditingMappingId(null);
    setEditingLabel('');
  };

  const handleSaveEdit = async (mappingId: number) => {
    if (!token) return;
    try {
      const updatedMapping = await updatePageMapping(token, mappingId, editingLabel || null);
      setPageMappings(prev => prev.map(m =>
        m.id === mappingId ? { ...m, label: updatedMapping.label } : m
      ));
      setEditingMappingId(null);
      setEditingLabel('');
    } catch (error) {
      console.error('Failed to update mapping:', error);
      alert('Failed to update page mapping. Please try again.');
    }
  };

  const handleStartEditRoom = () => {
    if (!room) return;
    setEditRoomNumber(room.number);
    setEditRoomName(room.name || '');
    setEditIsExterior(room.is_exterior);
    setEditingRoom(true);
  };

  const handleCancelEditRoom = () => {
    setEditingRoom(false);
    setEditRoomNumber('');
    setEditRoomName('');
    setEditIsExterior(false);
  };

  const displayEditRoomNumber = editIsExterior ? `EXT-${editRoomNumber}` : editRoomNumber;

  const handleEditRoomNumberChange = (value: string) => {
    const trimmedStart = value.trimStart();
    const hasExteriorPrefix = /^ext-/i.test(trimmedStart);
    const stripped = hasExteriorPrefix
      ? trimmedStart.replace(/^ext-/i, '').trimStart()
      : trimmedStart;

    setEditIsExterior(hasExteriorPrefix);
    setEditRoomNumber(stripped);
  };

  const handleSaveRoom = async () => {
    if (!token || !projectId || !roomId || !editRoomNumber.trim()) return;
    setSavingRoom(true);
    try {
      const updatedRoom = await updateRoom(
        token,
        parseInt(projectId),
        parseInt(roomId),
        {
          number: editRoomNumber.trim(),
          name: editRoomName.trim() || null,
          is_exterior: editIsExterior
        }
      );
      setRoom(updatedRoom);
      setEditingRoom(false);
      setEditRoomNumber('');
      setEditRoomName('');
    } catch (error) {
      console.error('Failed to update room:', error);
      alert('Failed to update room. Please try again.');
    } finally {
      setSavingRoom(false);
    }
  };

  if (!isAuthenticated || loading) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: '24px' }}>
        Loading...
      </div>
    );
  }

  if (!room) {
    return null;
  }

  const displayRoomNumber = room.is_exterior ? `EXT-${room.number}` : room.number;
  const qrUrl = project && room
    ? `${window.location.origin}/${project.slug}/${displayRoomNumber}`
    : `${window.location.origin}/room/${projectId}/${roomId}`;

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(`/admin/projects/${projectId}`)}
          style={{
            background: 'transparent',
            border: '1px solid #fff',
            color: '#fff',
            padding: '8px 16px',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          ← Back to Project
        </button>
        {editingRoom ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <div>
              <input
                type="text"
                value={displayEditRoomNumber}
                onChange={(e) => handleEditRoomNumberChange(e.target.value)}
                placeholder="Room Number"
                style={{
                  padding: '8px 12px',
                  fontSize: '16px',
                  borderRadius: '4px',
                  border: 'none',
                  width: '120px'
                }}
                autoFocus
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={editIsExterior}
                  onChange={(e) => setEditIsExterior(e.target.checked)}
                />
                Exterior
              </label>
            </div>
            <div>
              <input
                type="text"
                value={editRoomName}
                onChange={(e) => setEditRoomName(e.target.value)}
                placeholder="Room Name (optional)"
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: 'none',
                  width: '200px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveRoom();
                  if (e.key === 'Escape') handleCancelEditRoom();
                }}
              />
            </div>
            <button
              onClick={handleSaveRoom}
              disabled={!editRoomNumber.trim() || savingRoom}
              style={{
                background: '#28a745',
                color: '#fff',
                border: 'none',
                padding: '8px 16px',
                cursor: !editRoomNumber.trim() || savingRoom ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {savingRoom ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancelEditRoom}
              disabled={savingRoom}
              style={{
                background: 'transparent',
                border: '1px solid #fff',
                color: '#fff',
                padding: '8px 16px',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px' }}>
                Room {displayRoomNumber}
              </h1>
              {room.name && (
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                  {room.name}
                </p>
              )}
            </div>
            <button
              onClick={handleStartEditRoom}
              style={{
                background: 'transparent',
                border: '1px solid #fff',
                color: '#fff',
                padding: '6px 12px',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              Edit
            </button>
          </div>
        )}
      </header>

      <PendingUsersBanner />

      <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* QR Code Section */}
        <section style={{ marginBottom: isMobile ? '24px' : '48px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '16px' }}>QR Code</h2>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: isMobile ? '16px' : '24px',
            background: '#f8f9fa',
            padding: isMobile ? '16px' : '24px',
            borderRadius: '8px'
          }}>
            <a
              href={qrUrl}
              target="_blank"
              rel="noopener noreferrer"
              ref={qrRef}
              style={{
                display: 'block',
                background: '#fff',
                padding: isMobile ? '8px' : '16px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
              title="Click to open room page"
            >
              <QRCodeSVG value={qrUrl} size={isMobile ? 100 : 200} level="H" />
            </a>
            <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '14px', color: '#666' }}>
                Scan this QR code to view room plans on mobile devices.
              </p>
              <p style={{
                margin: '0 0 16px 0',
                fontSize: isMobile ? '10px' : '12px',
                fontFamily: 'monospace',
                wordBreak: 'break-all'
              }}>
                <a
                  href={qrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#007bff', textDecoration: 'none' }}
                >
                  {qrUrl}
                </a>
              </p>
              <button
                onClick={handleDownloadQR}
                style={{
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: isMobile ? '8px 16px' : '10px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: isMobile ? '12px' : '14px'
                }}
              >
                Download QR Code
              </button>
            </div>
          </div>
        </section>

        {/* Page Mappings Section */}
        <section>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h2 style={{ margin: 0 }}>Page Mappings</h2>
            <button
              onClick={() => setShowAddMapping(!showAddMapping)}
              style={{
                background: '#1a1a2e',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                cursor: 'pointer',
                borderRadius: '4px'
              }}
            >
              {showAddMapping ? 'Cancel' : '+ Add Mapping'}
            </button>
          </div>

          {showAddMapping && (
            <AddMappingForm
              projectId={parseInt(projectId!)}
              roomId={parseInt(roomId!)}
              token={token!}
              documentSets={documentSets}
              selectedDocumentSet={selectedDocumentSet}
              setSelectedDocumentSet={setSelectedDocumentSet}
              documents={documents}
              onMappingCreated={(mapping) => {
                setPageMappings(prev => [...prev, mapping]);
                setShowAddMapping(false);
              }}
            />
          )}

          {/* Current Mappings List */}
          {pageMappings.length === 0 ? (
            <p style={{ color: '#666' }}>No page mappings yet. Add mappings to link PDF pages to this room.</p>
          ) : isMobile ? (
            /* Mobile: Compact card list */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[...pageMappings]
                .sort((a, b) => {
                  const filenameA = a.document?.filename || '';
                  const filenameB = b.document?.filename || '';
                  return filenameA.localeCompare(filenameB);
                })
                .map(mapping => (
                  <div
                    key={mapping.id}
                    onClick={() => {
                      if (editingMappingId !== mapping.id) {
                        handleStartEdit(mapping);
                      }
                    }}
                    style={{
                      background: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      cursor: editingMappingId === mapping.id ? 'default' : 'pointer'
                    }}
                  >
                    {editingMappingId === mapping.id ? (
                      /* Editing mode */
                      <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                          {mapping.document?.filename || 'Unknown Document'}
                          {mapping.document && mapping.document.version > 1 && ` (v${mapping.document.version})`}
                        </div>
                        <input
                          type="text"
                          value={editingLabel}
                          onChange={(e) => setEditingLabel(e.target.value)}
                          placeholder="Enter label"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEdit(mapping.id);
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #007bff',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            marginBottom: '8px'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSaveEdit(mapping.id); }}
                            style={{
                              flex: 1,
                              background: '#28a745',
                              color: '#fff',
                              border: 'none',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                            style={{
                              flex: 1,
                              background: '#6c757d',
                              color: '#fff',
                              border: 'none',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteMapping(mapping.id); }}
                            style={{
                              flex: 1,
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display mode */
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#212529' }}>
                          {mapping.document?.filename || 'Unknown Document'}
                          {mapping.document && mapping.document.version > 1 && (
                            <span style={{ color: '#6c757d', fontWeight: 'normal' }}> (v{mapping.document.version})</span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          Page {mapping.page_number}
                          {mapping.label && <span> "{mapping.label}"</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            /* Desktop: Table view */
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Document</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', width: '100px' }}>Page</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Label</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...pageMappings]
                    .sort((a, b) => {
                      const filenameA = a.document?.filename || '';
                      const filenameB = b.document?.filename || '';
                      return filenameA.localeCompare(filenameB);
                    })
                    .map(mapping => (
                    <tr key={mapping.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {mapping.document?.filename || 'Unknown Document'}
                          {mapping.document && mapping.document.version > 1 && (
                            <span style={{
                              background: '#6c757d',
                              color: '#fff',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 'normal'
                            }}>
                              v{mapping.document.version}
                            </span>
                          )}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {mapping.page_number}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {editingMappingId === mapping.id ? (
                          <input
                            type="text"
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e.target.value)}
                            placeholder="Enter label"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(mapping.id);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '6px 10px',
                              border: '1px solid #007bff',
                              borderRadius: '4px',
                              fontSize: '14px',
                              boxSizing: 'border-box'
                            }}
                          />
                        ) : (
                          <span
                            onClick={() => handleStartEdit(mapping)}
                            style={{
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              display: 'inline-block',
                              minWidth: '100px',
                              color: mapping.label ? '#212529' : '#6c757d',
                              fontStyle: mapping.label ? 'normal' : 'italic'
                            }}
                            title="Click to edit label"
                          >
                            {mapping.label || 'Click to add label'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {editingMappingId === mapping.id ? (
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleSaveEdit(mapping.id)}
                              style={{
                                background: '#28a745',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                background: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleStartEdit(mapping)}
                              style={{
                                background: '#007bff',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteMapping(mapping.id)}
                              style={{
                                background: '#dc3545',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

interface AddMappingFormProps {
  projectId: number;
  roomId: number;
  token: string;
  documentSets: DocumentSet[];
  selectedDocumentSet: number | null;
  setSelectedDocumentSet: (id: number) => void;
  documents: DocumentType[];
  onMappingCreated: (mapping: PageMapping) => void;
}

function AddMappingForm({
  projectId,
  roomId,
  token,
  documentSets,
  selectedDocumentSet,
  setSelectedDocumentSet,
  documents,
  onMappingCreated
}: AddMappingFormProps) {
  const [selectedDocument, setSelectedDocument] = useState<number | null>(null);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [label, setLabel] = useState('');
  const [numPages, setNumPages] = useState<number>(0);

  useEffect(() => {
    if (documents.length > 0) {
      setSelectedDocument(documents[0].id);
    }
  }, [documents]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDocument) return;

    try {
      const mapping = await createPageMapping(
        token,
        roomId,
        selectedDocument,
        selectedPage,
        label || undefined
      );
      onMappingCreated(mapping);
      setSelectedPage(1);
      setLabel('');
    } catch (error) {
      console.error('Failed to create mapping:', error);
      alert('Failed to create page mapping. Please try again.');
    }
  };

  const selectedDoc = documents.find(d => d.id === selectedDocument);

  return (
    <div style={{
      background: '#f5f5f5',
      padding: '20px',
      marginBottom: '24px',
      borderRadius: '8px'
    }}>
      <form onSubmit={handleSubmit}>
        {/* Document Set Selection */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
            Document Set
          </label>
          <select
            value={selectedDocumentSet || ''}
            onChange={(e) => setSelectedDocumentSet(parseInt(e.target.value))}
            style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
          >
            {documentSets.map(ds => (
              <option key={ds.id} value={ds.id}>{ds.name}</option>
            ))}
          </select>
        </div>

        {/* Document Selection */}
        {documents.length === 0 ? (
          <p style={{ color: '#666' }}>No documents in this set. Please upload documents first.</p>
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Document
              </label>
              <select
                value={selectedDocument || ''}
                onChange={(e) => {
                  setSelectedDocument(parseInt(e.target.value));
                  setSelectedPage(1);
                }}
                style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
              >
                {documents.map(doc => (
                  <option key={doc.id} value={doc.id}>
                    {doc.filename} ({doc.page_count} pages)
                  </option>
                ))}
              </select>
            </div>

            {/* Page Preview */}
            {selectedDocument && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Select Page
                </label>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  background: '#fff',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <div style={{ flex: '0 0 300px', maxHeight: '400px', overflow: 'auto' }}>
                    <Document
                      file={`${API_BASE}/api/pdf/document/${selectedDocument}`}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      loading={<div>Loading PDF...</div>}
                      error={<div>Failed to load PDF</div>}
                    >
                      <Page
                        pageNumber={selectedPage}
                        width={280}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '4px' }}>
                        Page Number
                      </label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedPage(p => Math.max(1, p - 1))}
                          disabled={selectedPage <= 1}
                          style={{
                            padding: '8px 16px',
                            cursor: selectedPage <= 1 ? 'not-allowed' : 'pointer',
                            opacity: selectedPage <= 1 ? 0.5 : 1
                          }}
                        >
                          ←
                        </button>
                        <input
                          type="number"
                          min="1"
                          max={numPages}
                          value={selectedPage}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1 && val <= numPages) {
                              setSelectedPage(val);
                            }
                          }}
                          style={{ width: '80px', padding: '8px', textAlign: 'center' }}
                        />
                        <span>of {numPages}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedPage(p => Math.min(numPages, p + 1))}
                          disabled={selectedPage >= numPages}
                          style={{
                            padding: '8px 16px',
                            cursor: selectedPage >= numPages ? 'not-allowed' : 'pointer',
                            opacity: selectedPage >= numPages ? 0.5 : 1
                          }}
                        >
                          →
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '4px' }}>
                        Label (optional)
                      </label>
                      <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g., Floor Plan, Electrical, Plumbing"
                        style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <button
                      type="submit"
                      style={{
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        padding: '10px 20px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        width: '100%'
                      }}
                    >
                      Add Page Mapping
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
}
